/**
 * @file Cache global de Promesses Stripe.js indexées par compte Connect.
 * @module common.utils.stripeLoader
 *
 * Les PaymentIntent / SetupIntent créés en direct charges sur un compte Connect
 * doivent être confirmés avec une instance Stripe.js initialisée avec
 * `loadStripe(KEY, {stripeAccount: 'acct_xxx'})`. Sans ce paramètre, Stripe.js
 * cible la plateforme et ne trouve pas l'intent.
 *
 * Cache partagé au niveau du package commun : si plusieurs widgets ou pages
 * publiques cohabitent dans la même page navigateur, ils partagent la même
 * instance pour un compte Connect donné. Évite :
 *   - les téléchargements multiples de stripe.js
 *   - les `IntegrationError: Element not mounted` dus à des Elements montés
 *     contre des promesses différentes pour le même compte
 *
 * Clé `_platform` réservée au scope plateforme (non-Connect).
 */

import {loadStripe} from '@stripe/stripe-js';

const cache = new Map();

/**
 * Retourne la Promise<Stripe> mise en cache pour un compte Connect donné.
 *
 * La promesse est rejetée quand le script distant est injoignable (bloqueur de
 * contenu, réseau coupé). Dans ce cas l'entrée est retirée du cache : sinon un
 * échec ponctuel serait figé pour toute la durée de la page et un nouvel essai
 * de l'utilisateur relirait le même rejet.
 *
 * @param {string} [stripeAccount] - ID du compte Connect (`acct_xxx`). Omettre
 *   pour scope plateforme.
 * @return {Promise<Stripe>}
 */
export function getStripePromise(stripeAccount) {
  const key = stripeAccount || '_platform';
  if (!cache.has(key)) {
    const options = stripeAccount ? {stripeAccount} : undefined;
    const promise = loadStripe(process.env.REACT_APP_STRIPE_KEY, options).catch((error) => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, promise);
  }
  return cache.get(key);
}
